import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Autocomplete,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import api from '../../api/config';
import { IMPORT_BUSINESSES } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { getUserByMultiRoles } from '../../store/slices/userSlice';
import { buildAssignImportBusinessFormData } from '../../pages/importBusinesses/importBusinessApiHelpers';

function formatUserRoles(user) {
  const raw = user?.roles;
  if (Array.isArray(raw) && raw.length) {
    return raw.map((r) => (typeof r === 'string' ? r : r?.name)).filter(Boolean).join(', ');
  }
  if (user?.role?.name) return user.role.name;
  if (typeof user?.role === 'string') return user.role;
  return '';
}

function mapAssigneeOption(user) {
  const id = user?.uuid ?? user?.id;
  const name = user?.name || user?.username || 'Unnamed';
  const roles = formatUserRoles(user) || 'Lead Gen';
  return {
    id,
    name,
    roles,
    label: `${name} (${roles})`,
  };
}

const AssignImportBusinessDialog = ({
  open,
  selectedBusinessIds = [],
  onClose,
  onAssigned,
}) => {
  const dispatch = useDispatch();
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    setSelectedAssignee(null);
    setAssigneeLoading(true);
    dispatch(getUserByMultiRoles({ 'roles[]': ['Lead Gen'], paginate: 0 }))
      .unwrap()
      .then((res) => {
        const list = extractList(res?.data ?? res);
        setAssigneeOptions(list.map(mapAssigneeOption));
      })
      .catch(() => {
        toast.error('Failed to load Lead Gen users');
        setAssigneeOptions([]);
      })
      .finally(() => setAssigneeLoading(false));
  }, [open, dispatch]);

  const handleSubmit = async () => {
    if (!selectedAssignee?.id) {
      toast.error('Please select an assignee');
      return;
    }
    if (!selectedBusinessIds.length) {
      toast.error('Please select at least one data record');
      return;
    }

    setSubmitting(true);
    try {
      const formData = buildAssignImportBusinessFormData(
        selectedAssignee.id,
        selectedBusinessIds
      );
      const { data } = await api.post(IMPORT_BUSINESSES.ASSIGN_DIALING, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data?.message || 'Businesses assigned successfully');
      onAssigned?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign businesses');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Data</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedBusinessIds.length} record
            {selectedBusinessIds.length === 1 ? '' : 's'} selected for assignment
          </Typography>

          <Autocomplete
            options={assigneeOptions}
            getOptionLabel={(option) => option.label || option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedAssignee}
            onChange={(_, value) => setSelectedAssignee(value)}
            loading={assigneeLoading}
            disabled={submitting}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  {option.roles ? (
                    <Typography variant="caption" color="text.secondary">
                      {option.roles}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Assignee (Lead Gen) *"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {assigneeLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !selectedAssignee?.id || !selectedBusinessIds.length}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {submitting ? 'Assigning...' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignImportBusinessDialog;
